import React from 'react';
import { Button, Dialog, DialogTrigger, DialogContent, DialogFooter, TextField, TextArea, Text } from '@chatsdet/ui';
import type { ApplicationContextItem, ApplicationContextSection } from '../types';
import { SECTION_LABELS } from '../types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: {
    section: ApplicationContextSection;
    itemKey: string;
    itemLabel: string;
    content: string;
  }) => void;
  item?: ApplicationContextItem | null;
  section?: ApplicationContextSection;
  isSaving: boolean;
};

export function EditContextItemDialog({ isOpen, onClose, onSave, item, section, isSaving }: Props) {
  const [itemLabel, setItemLabel] = React.useState(item?.itemLabel ?? '');
  const [itemKey, setItemKey] = React.useState(item?.itemKey ?? '');
  const [content, setContent] = React.useState(item?.content ?? '');
  const selectedSection = item?.section ?? section ?? 'general';

  React.useEffect(() => {
    if (item) {
      setItemLabel(item.itemLabel);
      setItemKey(item.itemKey);
      setContent(item.content);
    } else {
      setItemLabel('');
      setItemKey('');
      setContent('');
    }
  }, [item]);

  const toSlug = (value: string) =>
    value.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '');

  const handleLabelChange = (value: string) => {
    setItemLabel(value);
    if (!item) {
      setItemKey(toSlug(value));
    }
  };

  const handleSave = () => {
    if (!itemLabel.trim() || !content.trim()) return;
    onSave({
      section: selectedSection,
      itemKey: itemKey || toSlug(itemLabel),
      itemLabel: itemLabel.trim(),
      content: content.trim(),
    });
  };

  return (
    <DialogTrigger isOpen={isOpen} onOpenChange={(open) => { if (!open) onClose(); }}>
      <Button className="hidden">Trigger</Button>
      <Dialog
        title={item ? 'Edit Context Item' : 'Add Context Item'}
        description={`Section: ${SECTION_LABELS[selectedSection]}`}
      >
        <DialogContent>
          <div className="space-y-4">
            <TextField
              label="Label"
              value={itemLabel}
              onChange={handleLabelChange}
              placeholder="e.g., Frontend Framework"
            />

            {itemKey && (
              <Text size="xs" className="text-muted-foreground -mt-2">
                Key: {itemKey}
              </Text>
            )}

            <TextArea
              label="Content"
              value={content}
              onChange={setContent}
              placeholder="Describe the detail, pattern, or behavior..."
              rows={4}
            />
          </div>
        </DialogContent>
        <DialogFooter>
          <Button variant="outline" onPress={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onPress={handleSave}
            isDisabled={!itemLabel.trim() || !content.trim() || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogFooter>
      </Dialog>
    </DialogTrigger>
  );
}
